import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient, getSessionTokenHeaders } from '../client'

export interface EnrollmentStartRequest {
  firstName: string
  lastName: string
  middleName?: string
  email: string
  phoneNumber: string
  dateOfBirth?: string
  addressLine1?: string
  city?: string
  state?: string
  zipCode?: string
  about?: string
  expectedLicenseMonth?: number
  expectedLicenseYear?: number
  enrollmentPath?: string
  referredBy?: string
  marketingConsent?: boolean
  smsConsent?: boolean
}

export interface EnrollmentStartResponse {
  id: number
  sessionToken: string
  enrollmentPath: string
  status: string
  currentStep: number
  userId: number | null
  userEmail: string | null
  firstName: string | null
  lastName: string | null
  email: string | null
  phoneNumber: string | null
  dateOfBirth: string | null
  ssnLastFour: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  driversLicenseNumber: string | null
  driversLicenseState: string | null
  driversLicenseIssueDate: string | null
  driversLicenseExpiry: string | null
  currentLicenseClass: string | null
  hasCdlPermit: boolean
  cdlPermitExpiry: string | null
  hasMedicalCertificate: boolean
  medicalCertificateExpiry: string | null
  hasFelonyConviction: boolean
  felonyDetails: string | null
  hasDuiHistory: boolean
  duiDetails: string | null
  usCitizen: boolean
  workAuthorization: boolean
  veteranStatus: boolean
  trainingLocationExternalId: string | null
  programExternalId: string | null
  preferredTransmission: string | null
  prefersWeekendClasses: boolean
  preferredStartDate: string | null
  confirmedStartDate: string | null
  tuitionAmount: number | null
  depositAmount: number | null
  discountAmount: number | null
  discountCode: string | null
  totalAmount: number | null
  amountPaid: number | null
  balanceDue: number | null
  englishTestScore: number | null
  englishTestPassed: boolean
  englishTestCompletedAt: string | null
  enrollmentAgreementSigned: boolean
  enrollmentAgreementSignedAt: string | null
  paymentPlanSigned: boolean
  paymentPlanSignedAt: string | null
  emergencyContactName: string | null
  emergencyContactPhone: string | null
  emergencyContactRelationship: string | null
  marketingConsent: boolean
  smsConsent: boolean
  utmSource: string | null
  utmMedium: string | null
  utmCampaign: string | null
  startedAt: string | null
  submittedAt: string | null
  approvedAt: string | null
  expiresAt: string | null
  lastActivityAt: string | null
  notes: string | null
  expectedLicenseMonth: number | null
  expectedLicenseYear: number | null
  endorsements: string[] | null
  latestQuote: {
    programExternalId: string | null
    programName: string | null
    locationExternalId: string | null
    locationName: string | null
    tuitionAmount: number
    registrationFee: number
    depositAmount: number
    minDepositAmount: number
    discountAmount: number
    discountCode: string | null
    totalAmount: number
    currency: string | null
    lineItems: Array<{
      code: string | null
      label: string | null
      amount: number
    }> | null
  } | null
  createdAt: string | null
  updatedAt: string | null
  contactExternalId: string | null
}

export const startEnrollmentRequest = async (
  data: EnrollmentStartRequest
): Promise<EnrollmentStartResponse> => {
  const response = await apiClient.post<EnrollmentStartResponse>('/api/v1/enrollment/public/start', {
    ...data
  })

  return response.data
}

export const submitSponsoredEnrollmentStepRequest = async ({
  sessionToken,
  data
}: {
  sessionToken: string
  data: EnrollmentStartRequest
}): Promise<EnrollmentStartResponse> => {
  const response = await apiClient.put<EnrollmentStartResponse>(
    `/api/v1/b2b-enrollment/session/${encodeURIComponent(sessionToken)}/step`,
    data,
    {
      headers: getSessionTokenHeaders(sessionToken)
    }
  )

  return response.data
}

export const useStartEnrollment = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: startEnrollmentRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
    }
  })
}

export const useSubmitSponsoredEnrollmentStep = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: submitSponsoredEnrollmentStepRequest,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['enrollment'] })
    }
  })
}
